import type {
  ASTPath,
  ImportSpecifier,
  JSCodeshift,
  JSXAttribute,
  JSXElement,
  Node,
} from 'jscodeshift';

export interface ReporterOptions {
  jscodeshift: JSCodeshift;
  issues: string[];
}

/**
 * CodemodReporter is a utility class for reporting issues found during codemod transformations.
 * It provides methods to report issues related to JSX elements, props, and attributes.
 *
 * @example
 * ```typescript
 * const issues: string[] = [];
 * const reporter = createReporter(j, issues);
 *
 * // Report a deprecated prop
 * reporter.reportDeprecatedProp(buttonElement, 'flat', 'variant="text"');
 *
 * // Report complex expression that needs review
 * reporter.reportAmbiguousExpression(element, 'size');
 *
 * // Auto-detect common issues
 * reporter.reportAttributeIssues(element);
 * ```
 */
export class CodemodReporter {
  private readonly j: JSCodeshift;
  private readonly issues: string[];

  constructor(options: ReporterOptions) {
    this.j = options.jscodeshift;
    this.issues = options.issues;
  }

  /**
   * Reports an issue with a JSX element
   */
  reportElement(element: JSXElement | ASTPath<JSXElement>, reason: string): void {
    const node = this.getNode(element);
    const componentName = this.getComponentName(node);
    const line = this.getLineNumber(node);

    this.addIssue(`Manual review required: <${componentName}> at line ${line} ${reason}.`);
  }

  /**
   * Reports an issue with a specific prop
   */
  reportProp(element: JSXElement | ASTPath<JSXElement>, propName: string, reason: string): void {
    const node = this.getNode(element);
    const componentName = this.getComponentName(node);
    const line = this.getLineNumber(node);

    this.addIssue(
      `Manual review required: prop "${propName}" on <${componentName}> at line ${line} ${reason}.`,
    );
  }

  /**
   * Reports an issue with a JSX attribute directly
   */
  reportAttribute(
    attr: JSXAttribute,
    element: JSXElement | ASTPath<JSXElement>,
    reason?: string,
  ): void {
    const node = this.getNode(element);
    const componentName = this.getComponentName(node);
    const propName = this.getAttributeName(attr);
    const line = this.getLineNumber(attr) || this.getLineNumber(node);

    const defaultReason = this.getAttributeReason(attr);
    const finalReason = reason || defaultReason;

    this.addIssue(
      `Manual review required: prop "${propName}" on <${componentName}> at line ${line} ${finalReason}.`,
    );
  }

  /**
   * Reports spread props on an element
   */
  reportSpreadProps(element: JSXElement | ASTPath<JSXElement>): void {
    this.reportElement(element, 'contains spread props that need manual review');
  }

  /**
   * Reports conflicting prop and children
   */
  reportPropWithChildren(element: JSXElement | ASTPath<JSXElement>, propName: string): void {
    this.reportProp(
      element,
      propName,
      `conflicts with children - both "${propName}" prop and children are present`,
    );
  }

  /**
   * Reports unsupported prop value
   */
  reportUnsupportedValue(
    element: JSXElement | ASTPath<JSXElement>,
    propName: string,
    value: string,
  ): void {
    this.reportProp(element, propName, `has unsupported value "${value}"`);
  }

  /**
   * Reports ambiguous expression in prop
   */
  reportAmbiguousExpression(element: JSXElement | ASTPath<JSXElement>, propName: string): void {
    this.reportProp(element, propName, 'contains a complex expression that needs manual review');
  }

  /**
   * Reports ambiguous children (like dynamic icons)
   */
  reportAmbiguousChildren(element: JSXElement | ASTPath<JSXElement>, childType = 'content'): void {
    this.reportElement(element, `contains ambiguous ${childType} that needs manual review`);
  }

  /**
   * Reports deprecated prop usage
   */
  reportDeprecatedProp(
    element: JSXElement | ASTPath<JSXElement>,
    propName: string,
    alternative?: string,
  ): void {
    const suggestion = alternative ? ` Use ${alternative} instead` : '';
    this.reportProp(element, propName, `is deprecated${suggestion}`);
  }

  /**
   * Reports missing required prop
   */
  reportMissingRequiredProp(element: JSXElement | ASTPath<JSXElement>, propName: string): void {
    this.reportProp(element, propName, 'is required but missing');
  }

  /**
   * Reports conflicting props
   */
  reportConflictingProps(element: JSXElement | ASTPath<JSXElement>, propNames: string[]): void {
    const propList = propNames.map((name) => `"${name}"`).join(', ');
    this.reportElement(element, `has conflicting props: ${propList} cannot be used together`);
  }

  /**
   * Auto-detects and reports common attribute issues
   */
  reportAttributeIssues(element: JSXElement | ASTPath<JSXElement>): void {
    const node = this.getNode(element);
    const { attributes } = node.openingElement;

    if (!attributes) return;

    // Check for spread props
    if (attributes.some((attr) => attr.type === 'JSXSpreadAttribute')) {
      this.reportSpreadProps(element);
    }

    // Check for complex expressions in attributes
    attributes.forEach((attr) => {
      if (attr.type === 'JSXAttribute' && attr.value?.type === 'JSXExpressionContainer') {
        this.reportAttribute(attr, element);
      }
    });
  }

  /**
   * Finds and reports instances of components that are under an alias (imported with a different name)
   */
  reportAliases(element: JSXElement | ASTPath<JSXElement>): void {
    this.reportElement(element, 'is used via an import alias and needs manual review');
  }

  /**
   * Finds and reports instances of non-DS import declarations that conflict with the component name
   */
  reportConflictingImports(node: ImportSpecifier): void {
    this.addIssue(
      `Manual review required: Non-WDS package resulting in an import conflict at line ${this.getLineNumber(node)}.`,
    );
  }

  /**
   * Reports enum usage for future conversion tracking
   */
  reportEnumUsage(
    element: JSXElement | ASTPath<JSXElement>,
    propName: string,
    enumValue: string,
  ): void {
    this.reportProp(
      element,
      propName,
      `uses enum value "${enumValue}" which has been preserved but should be migrated to a string literal in the future`,
    );
  }

  // Private helper methods
  private getNode(element: JSXElement | ASTPath<JSXElement>): JSXElement {
    return 'node' in element ? element.node : element;
  }

  private getComponentName(node: JSXElement): string {
    const { name } = node.openingElement;
    if (name.type === 'JSXIdentifier') {
      return name.name;
    }
    // Handle JSXMemberExpression, JSXNamespacedName, etc.
    return this.j(name).toSource();
  }

  private getLineNumber(node: JSXElement | JSXAttribute | Node): string {
    return node.loc?.start.line?.toString() || 'unknown';
  }

  private getAttributeName(attr: JSXAttribute): string {
    if (attr.name.type === 'JSXIdentifier') {
      return attr.name.name;
    }
    return this.j(attr.name).toSource();
  }

  private getAttributeReason(attr: JSXAttribute): string {
    if (!attr.value) return 'has no value';

    if (attr.value.type === 'JSXExpressionContainer') {
      const expr = attr.value.expression;
      const expressionType = expr.type.replace('Expression', '').toLowerCase();

      // Show actual value for simple cases
      if (expr.type === 'Identifier' || expr.type === 'MemberExpression') {
        const valueText = this.j(expr).toSource();
        return `contains a ${expressionType} (${valueText})`;
      }

      return `contains a complex ${expressionType} expression`;
    }

    return 'needs manual review';
  }

  private addIssue(message: string): void {
    this.issues.push(message);
  }
}

export const createReporter = (j: JSCodeshift, issues: string[]): CodemodReporter => {
  return new CodemodReporter({ jscodeshift: j, issues });
};
